import React from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Eye, Award, Link2, Target, Users, Globe, Trophy, Calendar, ArrowRight, CheckCircle2 } from 'lucide-react';
import ContributorForm from '../components/ContributorForm';
import AnimatedStat from '../components/AnimatedStat';
import { mockData } from '../mock';

const ContributorLanding = () => {
  const iconMap = {
    eye: Eye,
    award: Award,
    link: Link2,
    target: Target,
    users: Users,
    globe: Globe,
    trophy: Trophy,
    calendar: Calendar
  };

  const scrollToForm = () => {
    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="contributor-landing">
      {/* Header */}
      <header className="landing-header">
        <div className="header-container">
          <a href="https://www.kitco.com" target="_blank" rel="noopener noreferrer" className="logo-section">
            <img 
              src="https://customer-assets.emergentagent.com/job_metals-voices/artifacts/df99kqqi_Kitco_News_White_H.png" 
              alt="Kitco News" 
              className="logo-image"
            />
            <span className="logo-subtitle">CONTRIBUTOR PROGRAM</span>
          </a>
          <nav className="header-nav">
            <a href="#benefits" className="nav-link">Benefits</a>
            <a href="#process" className="nav-link">Process</a>
            <a href="#faq" className="nav-link">FAQ</a>
            <Button onClick={scrollToForm} className="cta-button">Apply Now</Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              THE WORLD'S NUMBER ONE SOURCE
              <span className="hero-subtitle">FOR PRECIOUS METALS COMMENTARY</span>
            </h1>
            <p className="hero-description">
              <strong>Join the leading platform</strong> for precious metals, mining, and cryptocurrency analysis. <strong>Become a contributor</strong> and share your expertise with millions of engaged readers on Kitco.com worldwide.
            </p>
            <Button onClick={scrollToForm} size="lg" className="hero-cta">
              Become a Contributor
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
        <div className="hero-overlay"></div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {mockData.stats.map((stat) => {
            const IconComponent = iconMap[stat.icon];
            return (
              <AnimatedStat
                key={stat.id}
                value={stat.value}
                label={stat.label}
                icon={IconComponent}
              />
            );
          })}
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" id="benefits">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">ABOUT KITCO NEWS</h2>
            <p className="section-description">
              For over 45 years, Kitco News has been the world's most trusted source for precious metals market information, analysis, and commentary. With 17M+ yearly unique visitors across 170+ countries, Kitco.com reaches millions of investors, traders, and industry professionals globally—making us the #1 destination for precious metals and cryptocurrency insights.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-container">
          <h2 className="section-title">WHY CONTRIBUTE TO KITCO NEWS?</h2>
          <div className="benefits-grid">
            {mockData.benefits.map((benefit) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <Card key={benefit.id} className="benefit-card">
                  <div className="benefit-icon-wrapper">
                    <IconComponent className="benefit-icon" size={32} />
                  </div>
                  <h3 className="benefit-title">{benefit.title}</h3>
                  <p className="benefit-description">{benefit.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section" id="process">
        <div className="section-container">
          <h2 className="section-title">HOW TO BECOME A CONTRIBUTOR</h2>
          <p className="section-description center">
            Our streamlined application process makes it easy to get started
          </p>
          <div className="process-grid">
            {mockData.process.map((item, index) => (
              <div key={item.id} className="process-card">
                <div className="process-number">{index + 1}</div>
                <div className="process-content">
                  <h3 className="process-title">{item.title}</h3>
                  <p className="process-description">{item.description}</p>
                </div>
                {index < mockData.process.length - 1 && (
                  <ArrowRight className="process-arrow" size={24} />
                )}
              </div>
            ))}
          </div>
          <div className="process-cta">
            <Button onClick={scrollToForm} size="lg" className="process-cta-button">
              Start Your Application
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="form-section" id="application-form">
        <div className="section-container">
          <div className="form-header">
            <h2 className="section-title">CONTRIBUTOR APPLICATION</h2>
            <p className="section-description center">
              Complete the form below to apply. Please provide detailed information to help us understand your expertise and writing style.
            </p>
          </div>
          <div className="form-wrapper">
            <ContributorForm />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="section-container">
          <h2 className="section-title">FREQUENTLY ASKED QUESTIONS</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            {mockData.faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                <AccordionContent className="faq-answer">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">READY TO SHARE YOUR EXPERTISE?</h2>
          <p className="cta-description">
            Join our community of contributors and reach millions of readers on Kitco.com interested in precious metals, mining, and cryptocurrency markets.
          </p>
          <Button onClick={scrollToForm} size="lg" className="cta-button-large">
            Apply to Become a Contributor
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src="https://customer-assets.emergentagent.com/job_metals-voices/artifacts/df99kqqi_Kitco_News_White_H.png" 
                alt="Kitco News" 
                className="footer-logo-image"
              />
              <p className="footer-tagline">The World's Premier Source for Precious Metals Information</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Quick Links</h4>
                <a href="https://www.kitco.com" target="_blank" rel="noopener noreferrer" className="footer-link">Kitco.com</a>
                <a href="https://corp.kitco.com/media.html" target="_blank" rel="noopener noreferrer" className="footer-link">About Kitco</a>
                <a href="https://www.kitco.com/advertising" target="_blank" rel="noopener noreferrer" className="footer-link">Advertise</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Kitco Metals Inc. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContributorLanding;
